import { useEffect, useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import { cn } from '@/lib/cn';
import { PuntoDeColor } from './piezas';
import {
  useBorrarFechaEspecial, useCrearFechaEspecial, useFechasEspeciales, type FechaEspecial,
} from './api';

/**
 * FechasEspeciales.tsx — la opción 6: festivos, cierres, campañas. Solo gestión.
 *
 * ## Una fecha que no se repite pide su año
 *
 * El vanilla tomaba en silencio el año **del mes que se estuviera mirando**:
 * quien añadía «Cierre de nómina» con el calendario en diciembre de 2026 la
 * guardaba en 2026 sin que nada lo dijera. Ahora el año es un campo, y solo
 * aparece cuando la fecha no es anual.
 *
 * ## El color es una marca
 *
 * Ver `PuntoDeColor`: el texto va en la tinta normal. El color por defecto,
 * `#0D2B4E`, es el mismo que pone el servidor cuando no llega ninguno.
 *
 * ## Borrar se confirma
 *
 * El vanilla no preguntaba, y lo explicaba: su lista vivía dentro de un modal y
 * el modal era uno solo, así que una confirmación la habría pisado. En un panel
 * no pasa. La baja es lógica, pero la fecha desaparece del calendario de todos.
 * Y el botón tiene nombre: la papelera vanilla no llevaba ni `aria-label` ni
 * `title`.
 */

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const COLOR_POR_DEFECTO = '#0D2B4E';

/** Los días de cada mes con 2024, que es bisiesto: el mismo criterio que
 *  `_validate_month_day` en el servidor, que acepta el 29 de febrero. */
const diasDelMes = (mes: number) => new Date(2024, mes, 0).getDate();

const dosDigitos = (n: number) => String(n).padStart(2, '0');

export function FechasEspeciales({
  abierto, anioSugerido, onCerrar,
}: {
  abierto: boolean;
  anioSugerido: number;
  onCerrar: () => void;
}) {
  const consulta = useFechasEspeciales(abierto);
  const crear = useCrearFechaEspecial();
  const borrar = useBorrarFechaEspecial();

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [mes, setMes] = useState(1);
  const [dia, setDia] = useState('1');
  const [anual, setAnual] = useState(true);
  const [anio, setAnio] = useState(String(anioSugerido));
  const [icono, setIcono] = useState('bi-stars');
  const [color, setColor] = useState(COLOR_POR_DEFECTO);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [borrando, setBorrando] = useState<FechaEspecial | null>(null);

  useEffect(() => {
    if (abierto) setAnio(String(anioSugerido));
  }, [abierto, anioSugerido]);

  function limpiarFormulario() {
    setNombre(''); setDescripcion(''); setDia('1'); setAnual(true);
    setIcono('bi-stars'); setColor(COLOR_POR_DEFECTO); setErrores({});
  }

  function validar(): boolean {
    const e: Record<string, string> = {};
    if (!nombre.trim()) e.nombre = 'La fecha necesita un nombre.';
    const d = Number(dia);
    if (!/^\d+$/.test(dia) || d < 1 || d > diasDelMes(mes)) {
      e.dia = `${MESES[mes - 1]} tiene días del 1 al ${diasDelMes(mes)}.`;
    }
    if (!anual && (!/^\d{4}$/.test(anio))) e.anio = 'Escribe el año con cuatro cifras.';
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  function agregar() {
    if (!validar()) return;
    crear.mutate(
      {
        name: nombre.trim(),
        description: descripcion.trim() || null,
        month: mes,
        day: Number(dia),
        year: anual ? null : Number(anio),
        icon: icono,
        color,
      },
      {
        onSuccess: () => { Utils.toast('Fecha especial agregada', 'success'); limpiarFormulario(); },
        onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo agregar la fecha', 'error'),
      },
    );
  }

  function confirmarBorrado() {
    if (!borrando) return;
    borrar.mutate(borrando.id, {
      onSuccess: () => { setBorrando(null); Utils.toast('Fecha especial eliminada', 'success'); },
      onError: (err) => {
        setBorrando(null);
        Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar la fecha', 'error');
      },
    });
  }

  const lista = consulta.data ?? [];

  return (
    <>
      <Drawer
        abierto={abierto}
        onCambio={(v) => { if (!v) onCerrar(); }}
        titulo="Fechas especiales"
        descripcion="Festivos, cierres y campañas que salen en el calendario de todo el portal."
        pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
      >
        <div className="flex flex-col gap-6">
          <section aria-labelledby="nueva-fecha" className="flex flex-col gap-4 rounded-sm border border-line p-4">
            <h3 id="nueva-fecha" className="text-3 font-semibold text-brand-ink">Nueva fecha</h3>

            <Field label="Nombre" error={errores.nombre}>
              <Input
                value={nombre}
                onChange={(e) => { setNombre(e.target.value); setErrores((x) => ({ ...x, nombre: '' })); }}
                placeholder="Ej.: Día de la familia"
              />
            </Field>

            <Field label="Descripción" opcional>
              <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Mes">
                <Select value={mes} onChange={(e) => { setMes(Number(e.target.value)); setErrores((x) => ({ ...x, dia: '' })); }}>
                  {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </Select>
              </Field>
              <Field label="Día" error={errores.dia}>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={diasDelMes(mes)}
                  value={dia}
                  onChange={(e) => { setDia(e.target.value); setErrores((x) => ({ ...x, dia: '' })); }}
                />
              </Field>
            </div>

            <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-3 text-ink">
              <input
                type="checkbox"
                checked={anual}
                onChange={(e) => setAnual(e.target.checked)}
                className="size-4 accent-brand"
              />
              Se repite cada año
            </label>

            {!anual && (
              <Field label="Año" error={errores.anio}>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={anio}
                  onChange={(e) => { setAnio(e.target.value); setErrores((x) => ({ ...x, anio: '' })); }}
                />
              </Field>
            )}

            <SelectorDeIcono id="fecha-especial-icono" valor={icono} onCambio={setIcono} respaldo="bi-stars" />

            <Field label="Color" id="fecha-especial-color" ayuda="Marca la fecha en el calendario. El texto siempre se lee en el color normal.">
              <div className="flex items-center gap-3">
                {/* `id` y `aria-describedby` a mano: un `<input>` crudo no los
                    toma del contexto de `Field`. */}
                <input
                  id="fecha-especial-color"
                  aria-describedby="fecha-especial-color-ayuda"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-11 w-20 cursor-pointer rounded-xs border border-field-border bg-surface p-1"
                />
                <span className="flex items-center gap-2 text-2 text-muted">
                  <PuntoDeColor color={color} /> {color.toUpperCase()}
                </span>
              </div>
            </Field>

            <div>
              <Button onClick={agregar} loading={crear.isPending}>
                <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar fecha
              </Button>
            </div>
          </section>

          <section aria-labelledby="fechas-guardadas">
            <h3 id="fechas-guardadas" className="mb-2 text-3 font-semibold text-brand-ink">
              Guardadas {lista.length > 0 && <span className="font-normal text-muted">({lista.length})</span>}
            </h3>
            {consulta.isLoading ? (
              <SkeletonFilas filas={3} columnas={2} />
            ) : consulta.isError ? (
              <EstadoError err={consulta.error} que="las fechas especiales" onReintentar={() => void consulta.refetch()} />
            ) : !lista.length ? (
              <p className="rounded-sm bg-sunken px-3 py-4 text-center text-2 text-muted">
                Todavía no hay fechas especiales.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-line rounded-sm border border-line">
                {lista.map((f) => (
                  <li key={f.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="w-24 shrink-0 text-2 tabular-nums text-ink-2">
                      {dosDigitos(f.day)}/{dosDigitos(f.month)}{f.recurring ? '' : `/${f.year}`}
                    </span>
                    <span className="flex min-w-0 flex-1 items-center gap-2 text-3 text-ink">
                      <PuntoDeColor color={f.color} />
                      <i className={cn('bi', /^bi-[a-z0-9-]+$/.test(f.icon ?? '') ? f.icon : 'bi-stars')} aria-hidden="true" />
                      <span className="truncate">{f.name}</span>
                    </span>
                    {f.recurring && <Badge tone="neutral">Cada año</Badge>}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Eliminar «${f.name}»`}
                      onClick={() => setBorrando(f)}
                    >
                      <i className="bi bi-trash3" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </Drawer>

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar la fecha especial"
        descripcion={borrando ? `«${borrando.name}» dejará de salir en el calendario de todo el portal.` : ''}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={confirmarBorrado}
      />
    </>
  );
}
